import { Box, Container, useTheme } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { t } from "@lingui/macro";
import SaveDiscardButtons from "../../Results/SaveDiscardButtons";
import {
  FourDPQuizResultsType,
  TrainingProgressionUpdateType,
  WahooWorkoutTypeFamily,
} from "@WahooFitness/cloud-client-types";
import Loader from "../../Results/Loader";
import useFourDPQuizResults from "./useFourDPQuizResults";
import ResultsHeader from "../../Results/ResultsHeader";
import { useInView } from "react-intersection-observer";
import ResultsHeaderBar from "../../Results/ResultsHeaderBar";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";
import StrengthWeaknessSection from "@/components/FitnessProfile/Shared/StrengthWeaknessSection";
import SnailSection from "@/components/FitnessProfile/Shared/SnailSection";

interface Props {
  results?: FourDPQuizResultsType;
  error?: string;
  setPage: Dispatch<SetStateAction<number>>;
  closeOnBack: boolean;
  initialTrainingProgression?: TrainingProgressionUpdateType;
}

const FourDPQuizResults = ({
  results,
  error,
  setPage,
  closeOnBack,
  initialTrainingProgression,
}: Props) => {
  const {
    resultsLoading,
    riderStrength,
    riderTypeData,
    riderWeakness,
    dimensions,
    legendItems,
    saveFitnessProfile,
    openInfoDrawer,
    openFitnessTestDrawer,
    discardResults,
    reloadHandler,
    analyticsContext,
  } = useFourDPQuizResults(setPage, results, closeOnBack, initialTrainingProgression);
  const { palette } = useTheme();

  const { ref: headerBottom, entry } = useInView({
    initialInView: true,
    trackVisibility: true,
    delay: 200,
  });

  return (
    <Box
      // Self-scrolling: the sticky save/discard bar provides its own bottom
      // clearance, so the shell's page-end spacer would double-lift it.
      className="wf-self-scrolling-page"
      bgcolor={palette.background.level1}
      minHeight="100dvh"
    >
      <GlobalHeader hasOwnHeader />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary
          isLoading={resultsLoading || !results}
          error={!!error}
          loadingComponent={<Loader message={t`Calculating your results...`} />}
          onReload={reloadHandler}
        >
          <Box display="flex" flexDirection="column" alignItems="center" height="100%">
            <Box
              sx={{
                position: "relative",
                width: "100vw",
                backgroundColor: palette.background.level1,
              }}
              display="flex"
              pt={2}
              alignItems="center"
              flexDirection="column"
            >
              <Box
                id="inner-container-top-section"
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                maxWidth="sm"
                width="100%"
                gap={1.5}
                pb={3}
              >
                <ResultsHeaderBar
                  athleteType={riderTypeData?.name}
                  showHeaderBar={entry && !entry?.isIntersecting}
                  athleteTypeIcon={riderTypeData?.icon}
                  handleEstimateClick={openFitnessTestDrawer}
                />
                <ResultsHeader
                  icon={riderTypeData?.iconFilled}
                  name={riderTypeData?.name}
                  handleEstimateClick={openFitnessTestDrawer}
                  profileType={WahooWorkoutTypeFamily.Cycling}
                />
                <Box ref={headerBottom} />
                <SnailSection
                  profileDescription={riderTypeData?.description}
                  workoutFamily={WahooWorkoutTypeFamily.Cycling}
                  hasProfile={!!results}
                  dimensions={dimensions}
                  legendItems={legendItems}
                  openInfoDrawer={openInfoDrawer}
                  isProfileEstimate={true}
                  isProfileManual={false}
                  openEstimateExplanationDrawer={openFitnessTestDrawer}
                />
                <StrengthWeaknessSection
                  strength={riderStrength}
                  weakness={riderWeakness}
                  analyticsContext={analyticsContext}
                  defaultExpanded={true}
                  disabled={true}
                />
              </Box>
              <SaveDiscardButtons onSave={saveFitnessProfile} onDiscard={discardResults} />
            </Box>
          </Box>
        </ErrorBoundary>
      </Container>
    </Box>
  );
};

export default FourDPQuizResults;
